import { useCallback, useRef } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	getAllLinks,
	getChannelIdFromSlackLink,
	isMailtoLink,
	isSlackLink,
} from './utils/links.ts';

/**
 * Classify a list of URLs into slack/mailto buckets, excluding any that appear
 * in `startingLinks`.
 */
const classifyNewLinks = (
	urls: Iterable<string>,
	startingLinks: Set<string>,
): { pastedSlackLinks: string[]; pastedMailtoLinks: string[] } => {
	const pastedSlackLinks: string[] = [];
	const pastedMailtoLinks: string[] = [];

	for (const link of urls) {
		if (startingLinks.has(link)) {
			continue;
		}
		if (isSlackLink(link)) {
			pastedSlackLinks.push(link);
		} else if (isMailtoLink(link)) {
			pastedMailtoLinks.push(link);
		}
	}

	return { pastedSlackLinks, pastedMailtoLinks };
};

export const useDetectPastedThirdPartyLinks = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId, orgId } = useSessionData();

	// Track what URLs existed when editor opened (initial state)
	// This enables URL-level delta detection
	const initialLinksRef = useRef<Set<string>>(new Set());

	/**
	 * Set the initial content when editor opens
	 */
	const setInitialLinks = useCallback((adfValue: ADFEntity | null | undefined) => {
		if (!fg('twcg-853-3p-in-1p-aa-test-gate') || !adfValue) {
			return;
		}
		const initialLinks = getAllLinks(adfValue);
		initialLinksRef.current = initialLinks;
	}, []);

	const fireAnalytics = useCallback(
		({
			pastedSlackLinks,
			pastedMailtoLinks,
		}: {
			pastedSlackLinks: string[];
			pastedMailtoLinks: string[];
		}) => {
			// 3P in 1P exp eligibility check flow
			if (
				(pastedSlackLinks.length === 0 && pastedMailtoLinks.length === 0) ||
				!fg('twcg-853-3p-in-1p-aa-test-gate')
			) {
				return;
			}

			// Check cohort, fire exposure and early return for control
			if (!expValEquals('twcg-853-3p-in-1p-aa-test', 'isEnabled', true)) {
				return;
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'pasted',
					actionSubject: '3PLink',
					actionSubjectId: 'editorPaste',
					source: 'inviteOnLinkInsert',
					attributes: {
						slackLinks: pastedSlackLinks,
						channelIds: pastedSlackLinks.map(getChannelIdFromSlackLink),
						mailtoLinkCount: pastedMailtoLinks.length,
						orgId,
						accountId: userId,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, userId, orgId],
	);

	/**
	 * Save-time strategy: traverses the full ADF document to find links not
	 * present in `startingLinks`. Used by comment editors and usePublish, which
	 * already receive the full saved ADF at their natural save point.
	 */
	const handleSave = useCallback(
		(content: ADFEntity) => {
			if (!content) {
				return;
			}
			const classifiedLinks = classifyNewLinks(getAllLinks(content), initialLinksRef.current);
			fireAnalytics(classifiedLinks);
		},
		[fireAnalytics],
	);

	/**
	 * Paste-time strategy: accepts a pre-extracted list of URLs from only the
	 * pasted slice (no full-document traversal). Used by the live-page editor.
	 */
	const handlePastedLinks = (pastedUrls: string[]) => {
		// Early return if no pasted links or gate is off
		if (pastedUrls.length === 0) {
			return;
		}

		// Get the starting links from the ref
		const startingLinks = initialLinksRef.current;

		const classifiedLinks = classifyNewLinks(pastedUrls, startingLinks);
		fireAnalytics(classifiedLinks);

		// Add new links to the initial links ref so the flag doesn't spam on duplicate links
		Object.values(classifiedLinks)
			.flat()
			.forEach((link) => initialLinksRef.current.add(link));
	};

	return { handleSave, handlePastedLinks, setInitialLinks };
};
